"use client";

import { useRef } from "react";
import { Icon, Pill } from "@/components/Icons";
import { useStock } from "@/context/StockContext";

function Steps({ n }: { n: number }) {
  const s = ["Photo du BL", "Vérification", "Entrée en stock"];
  return (
    <div className="steps">
      {s.map((t, i) => (
        <span key={t} className={`step ${i + 1 === n ? "on" : ""} ${i + 1 < n ? "done" : ""}`}>
          <i>{i + 1 < n ? "✓" : i + 1}</i>
          {t}
        </span>
      ))}
    </div>
  );
}

export function ReceptionView() {
  const {
    produits,
    reception,
    setReception,
    lancerLecture,
    validerReception,
    resetReception,
    go,
  } = useStock();
  const fileRef = useRef<HTMLInputElement>(null);
  const r = reception;

  if (r.etape === 1) {
    return (
      <>
        <Steps n={1} />
        <div className="capture">
          <div className="card" style={{ display: "grid", gap: 14, alignContent: "start" }}>
            <div className="field">
              <label htmlFor="fourn">Fournisseur</label>
              <select
                id="fourn"
                value={r.fournisseur}
                onChange={(e) => setReception({ ...r, fournisseur: e.target.value })}
              >
                {["Grossiste A", "Grossiste B", "Autre fournisseur"].map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="blNum">N° du bon de livraison (facultatif)</label>
              <input id="blNum" type="text" placeholder="Lu automatiquement sur la photo" />
            </div>
            <p className="muted" style={{ fontSize: ".9rem" }}>
              La marchandise entre en stock dès la livraison. La facture du fournisseur, qui arrive
              en fin de mois, sert seulement au contrôle.
            </p>
          </div>
          <div className="drop">
            <Icon name="camera" />
            <div>
              <h3>Photographier le bon de livraison</h3>
              <p className="muted" style={{ fontSize: ".9rem", marginTop: 4 }}>
                L&apos;IA lit les références et les quantités. Tu vérifies avant de valider.
              </p>
            </div>
            <label className="btn primary big" htmlFor="photoBL">
              <Icon name="camera" /> Prendre la photo
            </label>
            <input
              id="photoBL"
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) lancerLecture(URL.createObjectURL(f));
              }}
            />
            <button className="btn small" type="button" onClick={() => lancerLecture(null)}>
              Essayer avec un exemple
            </button>
          </div>
        </div>
      </>
    );
  }

  if (r.etape === "lecture") {
    return (
      <>
        <Steps n={2} />
        <div className="card reading">
          <div
            className="ph"
            style={r.photo ? { backgroundImage: `url('${r.photo}')` } : undefined}
          >
            {!r.photo && (
              <div className="fake-bl">
                {[90, 60, 80, 70, 85, 55, 75, 65, 80].map((w, i) => (
                  <span key={i} style={{ width: `${w}%` }} />
                ))}
              </div>
            )}
          </div>
          <div>
            <h2>Lecture du bon de livraison…</h2>
            <p className="muted" style={{ marginTop: 6 }}>
              L&apos;IA repère les lignes, les références et les quantités, puis les compare à ton
              catalogue.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (r.etape === 2) {
    const conf = {
      sur: <Pill kind="ok">Reconnu</Pill>,
      verifier: <Pill kind="warn">À vérifier</Pill>,
      inconnu: <Pill kind="bad">Inconnu</Pill>,
    };
    return (
      <>
        <Steps n={2} />
        <div className="card-h">
          <div>
            <h2>
              {r.fournisseur} · {r.lignes.length} lignes lues
            </h2>
            <p className="muted" style={{ fontSize: ".9rem" }}>
              Corrige ce qui doit l&apos;être, puis valide.
            </p>
          </div>
        </div>
        <div className="tbl-wrap rcpt">
          <table>
            <thead>
              <tr>
                <th>Lu sur le BL</th>
                <th>Article du stock</th>
                <th className="r">Qté reçue</th>
                <th>Lecture</th>
              </tr>
            </thead>
            <tbody>
              {r.lignes.map((l, i) => (
                <tr key={i}>
                  <td>
                    <span className="lu">{l.lu}</span>
                  </td>
                  <td>
                    <select
                      aria-label={`Article ligne ${i + 1}`}
                      value={l.ref}
                      onChange={(e) => {
                        const ref = e.target.value;
                        setReception({
                          ...r,
                          lignes: r.lignes.map((x, j) =>
                            j === i
                              ? { ...x, ref, conf: ref && ref !== "__new" ? "sur" : x.conf }
                              : x,
                          ),
                        });
                      }}
                    >
                      <option value="">— Choisir l&apos;article —</option>
                      {produits.map((p) => (
                        <option key={p.ref} value={p.ref}>
                          {p.nom} · {p.ref}
                        </option>
                      ))}
                      <option value="__new">+ Créer un nouvel article</option>
                    </select>
                  </td>
                  <td className="r">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={l.qte}
                      aria-label={`Quantité ligne ${i + 1}`}
                      onChange={(e) => {
                        const qte = Math.max(0, parseInt(e.target.value, 10) || 0);
                        setReception({
                          ...r,
                          lignes: r.lignes.map((x, j) => (j === i ? { ...x, qte } : x)),
                        });
                      }}
                    />
                  </td>
                  <td>{conf[l.conf]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 14,
            flexWrap: "wrap",
          }}
        >
          <button className="btn" type="button" onClick={resetReception}>
            Annuler
          </button>
          <button className="btn primary" type="button" onClick={validerReception}>
            <Icon name="check" /> Valider la réception
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Steps n={3} />
      <div className="card summary">
        <div className="check">
          <Icon name="check" />
        </div>
        <h2>Réception enregistrée</h2>
        <p className="muted">
          {r.resume!.lignes} ligne{r.resume!.lignes > 1 ? "s" : ""} · {r.resume!.unites} unités
          entrées en stock depuis {r.fournisseur}.
        </p>
        {!!r.resume!.ignorees && (
          <p className="muted">{r.resume!.ignorees} ligne non associée, laissée de côté.</p>
        )}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn primary" type="button" onClick={resetReception}>
            Nouvelle réception
          </button>
          <button className="btn" type="button" onClick={() => go("stock")}>
            Voir le stock
          </button>
        </div>
      </div>
    </>
  );
}
